import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Store, Plus, RefreshCw, TrendingUp, AlertTriangle, CheckCircle2, Loader2, Wallet, Coins } from "lucide-react";
import api, { friendlyError } from "@/lib/api";
import Layout from "@/components/Layout";
import { Card, Chip, Button, Skeleton, EmptyState, Toast, Input, Modal } from "@/ui";

const PLANS = ["starter", "growth", "scale"];
const STATUS_TONE = { done: "good", running: "info", failed: "bad", not_started: "muted" };

function SetupBar({ done, total }) {
  const pct = total ? Math.round((done / total) * 100) : 0;
  return (
    <div className="w-full h-2 rounded-full overflow-hidden" style={{ background: "#e2e8f0" }} data-testid="setup-bar">
      <div style={{ width: `${pct}%`, height: "100%", background: pct === 100 ? "var(--good)" : "var(--accent)", transition: "width var(--t-base) var(--ease-enter)" }} />
    </div>
  );
}

function ShopRow({ shop, canMoney, canEdit, onPlan, onRate, onRetry, busy }) {
  const [plan, setPlan] = useState(shop.plan || "starter");
  const [rate, setRate] = useState(shop.takeRatePercent ?? 0);
  const failed = shop.steps.filter((s) => s.status === "failed");
  return (
    <Card className="p-4" data-testid={`deck-shop-${shop.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Store size={18} strokeWidth={1.75} />
            <h3 className="text-base font-semibold truncate" data-testid={`deck-shop-name-${shop.id}`}>{shop.name}</h3>
            <Chip tone={STATUS_TONE[shop.setupStatus] || "muted"} data-testid={`deck-setup-status-${shop.id}`}>{shop.setupStatus.replace("_", " ")}</Chip>
          </div>
          <div className="mt-2 max-w-xs">
            <SetupBar done={shop.setupDone} total={shop.setupTotal} />
            <div className="text-xs mt-1" style={{ color: "var(--text-3)" }}>{shop.setupDone}/{shop.setupTotal} setup steps</div>
          </div>
        </div>
        <div className="text-right">
          {canMoney ? (
            <div className="flex gap-4">
              <div><div className="text-xs" style={{ color: "var(--text-3)" }}>Money moving</div><div className="text-sm font-semibold num" data-testid={`deck-money-${shop.id}`}>{shop.moneyMoved}</div></div>
              <div><div className="text-xs" style={{ color: "var(--text-3)" }}>Cost / order</div><div className="text-sm font-semibold num" data-testid={`deck-cpo-${shop.id}`}>{shop.costPerOrder}</div></div>
              <div><div className="text-xs" style={{ color: "var(--text-3)" }}>Credits</div><div className="text-sm font-semibold num">{shop.creditsUsed}</div></div>
            </div>
          ) : null}
        </div>
      </div>

      {canEdit ? (
        <div className="flex items-center gap-2 mt-3 flex-wrap">
          <span className="text-xs" style={{ color: "var(--text-3)" }}>Plan</span>
          <select className="select" value={plan} data-testid={`deck-plan-${shop.id}`} onChange={(e) => setPlan(e.target.value)} style={{ width: 120 }}>
            {PLANS.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          {plan !== shop.plan ? <Button data-testid={`deck-plan-save-${shop.id}`} onClick={() => onPlan(shop.id, plan)}>save plan</Button> : null}
          <span className="text-xs ml-2" style={{ color: "var(--text-3)" }}>Rate %</span>
          <Input value={String(rate)} data-testid={`deck-rate-${shop.id}`} inputMode="decimal" style={{ width: 70 }}
            onChange={(e) => setRate(e.target.value.replace(/[^0-9.]/g, ""))} />
          {String(rate) !== String(shop.takeRatePercent) ? <Button data-testid={`deck-rate-save-${shop.id}`} onClick={() => onRate(shop.id, rate)}>save rate</Button> : null}
        </div>
      ) : (
        <div className="text-sm mt-2" style={{ color: "var(--text-2)" }}>Plan <strong>{shop.plan}</strong> · rate <strong>{shop.takeRatePercent}%</strong></div>
      )}

      {failed.length && onRetry ? (
        <div className="mt-3 rounded-xl p-3" style={{ background: "#fef2f2" }} data-testid={`deck-failed-${shop.id}`}>
          {failed.map((s) => (
            <div key={s.key} className="flex items-center justify-between gap-2 py-1">
              <div className="text-sm" style={{ color: "var(--bad)" }}><AlertTriangle size={14} className="inline mr-1" />{s.label}{s.message ? ` — ${s.message}` : ""}</div>
              <Button data-testid={`deck-retry-${shop.id}-${s.key}`} loading={busy === `${shop.id}:${s.key}`} onClick={() => onRetry(shop.id, s.key)}>
                <RefreshCw size={14} strokeWidth={1.75} />retry
              </Button>
            </div>
          ))}
        </div>
      ) : null}
    </Card>
  );
}

export default function Overview() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");
  const [busy, setBusy] = useState("");
  const [wizard, setWizard] = useState(false);
  const [nw, setNw] = useState({ name: "", kind: "food", ownerEmail: "", plan: "starter", takeRatePercent: "5" });
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    try { const r = await api.get("/deck/overview"); setData(r.data); }
    catch (e) { setError(friendlyError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const flash = (m) => { setToast(m); setTimeout(() => setToast(""), 2500); };
  const onPlan = async (id, plan) => { try { await api.post(`/deck/shops/${id}/plan`, { plan }); flash("plan updated"); load(); } catch (e) { setError(friendlyError(e)); } };
  const onRate = async (id, rate) => { try { await api.post(`/deck/shops/${id}/rate`, { takeRatePercent: parseFloat(rate) }); flash("rate updated"); load(); } catch (e) { setError(friendlyError(e)); } };
  const onRetry = async (id, key) => { setBusy(`${id}:${key}`); try { await api.post(`/deck/shops/${id}/retry/${key}`); flash("step retried"); await load(); } catch (e) { setError(friendlyError(e)); } finally { setBusy(""); } };
  const createShop = async () => {
    if (!nw.name.trim() || !nw.ownerEmail.trim()) { setError("Add a shop name and the owner's email."); return; }
    setCreating(true);
    try {
      const r = await api.post("/deck/shops", { name: nw.name, kind: nw.kind, ownerEmail: nw.ownerEmail, plan: nw.plan, takeRatePercent: parseFloat(nw.takeRatePercent) });
      setWizard(false); setNw({ name: "", kind: "food", ownerEmail: "", plan: "starter", takeRatePercent: "5" });
      flash(`${r.data.name} created — guided setup started`); await load();
    } catch (e) { setError(friendlyError(e)); } finally { setCreating(false); }
  };

  return (
    <Layout>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <div>
          <h1 className="t-h1">Command Deck</h1>
          <p className="muted t-small">{data ? (data.canMoney ? "Every shop, how far it got, and the money moving through it." : "Shops and how far each one got through setup.") : ""}</p>
        </div>
        {data?.canAddShop ? (
          <Button variant="primary" data-testid="deck-add-shop" onClick={() => setWizard(true)}>
            <Plus size={16} strokeWidth={1.75} />add a shop
          </Button>
        ) : null}
      </div>

      {error ? <Toast tone="bad" data-testid="deck-error">{error}</Toast> : null}

      {!data ? <div className="stack" style={{ gap: 12 }}>{[0, 1, 2].map((i) => <Skeleton key={i} style={{ height: 96 }} />)}</div> : (
        <>
          {data.canMoney ? (
            <div className="grid grid-2 mb-5" style={{ gap: 12 }}>
              <Card className="p-4" data-testid="deck-total-money">
                <div className="flex items-center gap-2 text-sm" style={{ color: "var(--text-3)" }}><Wallet size={16} />Total money moving (30 days)</div>
                <div className="text-2xl font-bold num mt-1">{data.totalMoneyMoved}</div>
              </Card>
              <Card className="p-4" data-testid="deck-shop-count">
                <div className="flex items-center gap-2 text-sm" style={{ color: "var(--text-3)" }}><Store size={16} />Shops</div>
                <div className="text-2xl font-bold num mt-1">{data.shopCount}</div>
              </Card>
            </div>
          ) : null}

          {data.shops.length ? (
            <div className="stack" style={{ gap: 12 }} data-testid="deck-shops">
              {data.shops.map((s) => (
                <ShopRow key={s.id} shop={s} canMoney={data.canMoney} canEdit={data.canEditPlan}
                  onPlan={onPlan} onRate={onRate} onRetry={data.role !== "helper" ? onRetry : null} busy={busy} />
              ))}
            </div>
          ) : <EmptyState icon={Store} title="No shops yet" body="Shops you bring in will show up here." />}
        </>
      )}

      <Modal open={wizard} title="Add a shop" onClose={() => setWizard(false)}
        footer={<>
          <Button data-testid="wizard-cancel" onClick={() => setWizard(false)}>cancel</Button>
          <Button variant="primary" data-testid="wizard-create" loading={creating} onClick={createShop}>create & start setup</Button>
        </>}>
        <div className="stack" style={{ gap: 10 }}>
          <Input placeholder="Shop name" value={nw.name} data-testid="wizard-name" onChange={(e) => setNw({ ...nw, name: e.target.value })} />
          <Input placeholder="Owner's email (for sign-in)" value={nw.ownerEmail} data-testid="wizard-email" onChange={(e) => setNw({ ...nw, ownerEmail: e.target.value })} />
          <div className="flex gap-2 flex-wrap items-center">
            <span className="text-xs" style={{ color: "var(--text-3)" }}>Type</span>
            <select className="select" value={nw.kind} data-testid="wizard-kind" onChange={(e) => setNw({ ...nw, kind: e.target.value })}>
              <option value="food">Food</option><option value="retail">Retail</option>
            </select>
            <span className="text-xs" style={{ color: "var(--text-3)" }}>Plan</span>
            <select className="select" value={nw.plan} data-testid="wizard-plan" onChange={(e) => setNw({ ...nw, plan: e.target.value })}>
              {PLANS.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
            <span className="text-xs" style={{ color: "var(--text-3)" }}>Rate %</span>
            <Input value={nw.takeRatePercent} data-testid="wizard-rate" style={{ width: 70 }} onChange={(e) => setNw({ ...nw, takeRatePercent: e.target.value.replace(/[^0-9.]/g, "") })} />
          </div>
          <p className="text-xs" style={{ color: "var(--text-3)" }}>We'll apply the matching default assistant and start the guided setup automatically.</p>
        </div>
      </Modal>
      {toast ? <div className="toast-wrap"><Toast tone="good" data-testid="deck-toast">{toast}</Toast></div> : null}
    </Layout>
  );
}
